import { apiClient } from '@/lib/api-client';
import type {
  ApiResponse,
  Device,
  MeterReading,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  PaginatedResponse,
} from '@/types/api';

export type DeviceSortBy = 'unit' | 'createdAt' | 'lastSeen' | 'propertyName';
export type SortDir = 'asc' | 'desc';

export interface GetDevicesParams {
  search?: string;
  unitId?: string;
  propertyId?: string;
  buildingId?: string;
  page?: number;
  perPage?: number;
  sortBy?: DeviceSortBy;
  sortDir?: SortDir;
}

export async function getDevices(params: GetDevicesParams = {}): Promise<PaginatedResponse<Device>> {
  const response = await apiClient.get<PaginatedResponse<Device>>('/devices', { params });
  return response.data;
}

export async function checkDevEuiExists(devEui: string): Promise<ApiResponse<boolean>> {
  const response = await apiClient.get<ApiResponse<boolean>>('/devices/exists', { params: { devEui } });
  return response.data;
}

export async function getDevice(id: string): Promise<ApiResponse<Device>> {
  const response = await apiClient.get<ApiResponse<Device>>(`/devices/${id}`);
  return response.data;
}

export async function createDevice(data: CreateDeviceRequest): Promise<ApiResponse<Device>> {
  const response = await apiClient.post<ApiResponse<Device>>('/devices', data);
  return response.data;
}

export async function updateDevice(id: string, data: UpdateDeviceRequest): Promise<ApiResponse<null>> {
  const response = await apiClient.put<ApiResponse<null>>(`/devices/${id}`, data);
  return response.data;
}

export async function deleteDevice(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/devices/${id}`);
  return response.data;
}

export async function unassignDevice(id: string): Promise<ApiResponse<null>> {
  const response = await apiClient.post<ApiResponse<null>>(`/devices/${id}/unassign`);
  return response.data;
}

export async function lookupDeviceBySerial(serialNumber: string): Promise<ApiResponse<Device>> {
  const response = await apiClient.get<ApiResponse<Device>>('/devices/by-serial', { params: { serialNumber } });
  return response.data;
}

export interface GetDeviceMeterReadingsParams {
  page?: number;
  perPage?: number;
  unitId?: string;
  /**
   * Returns only readings whose timestamp is at-or-before this ISO string.
   * Combined with `perPage=1` this gives the most recent reading just
   * before a cutoff — used by the stats cards to compute exact 24h /
   * month-to-date deltas regardless of how many chart readings are loaded.
   */
  before?: string;
}

export async function getDeviceMeterReadings(
  deviceId: string,
  params: GetDeviceMeterReadingsParams = {}
): Promise<PaginatedResponse<MeterReading>> {
  const response = await apiClient.get<PaginatedResponse<MeterReading>>(`/devices/${deviceId}/readings`, { params });
  return response.data;
}

export async function checkAwsIotStatus(devEui: string): Promise<{ isProvisioned: boolean }> {
  const response = await apiClient.get<ApiResponse<{ isProvisioned: boolean }>>('/devices/aws-iot-status', { params: { devEui } });
  return response.data.data!;
}

export interface ProvisionAwsIotData {
  serialNumber: string;
  devEui: string;
  joinEui: string;
  appKey: string;
}

export async function provisionAwsIot(data: ProvisionAwsIotData): Promise<void> {
  await apiClient.post<ApiResponse<null>>('/devices/aws-iot-provision', data);
}

export enum DeviceMultiplier {
  OneToOne = 1,
  OneToTen = 10,
  OneToHundred = 100,
  OneToThousand = 1000,
}

export enum DeviceUnitOfMeasure {
  Gallons = 0,
  Liters = 1,
  CubicFeet = 2,
  CubicMeters = 3,
}

export interface UpdateDeviceConfigRequest {
  multiplier: DeviceMultiplier;
  unitOfMeasure: DeviceUnitOfMeasure;
}

export interface DeviceConfigDto {
  deviceId: string;
  multiplier: DeviceMultiplier;
  unitOfMeasure: DeviceUnitOfMeasure;
  effectiveAt: string;
}

export async function updateDeviceConfig(
  deviceId: string,
  data: UpdateDeviceConfigRequest,
): Promise<DeviceConfigDto> {
  const res = await apiClient.put<ApiResponse<DeviceConfigDto>>(`/devices/${deviceId}/config`, data);
  return res.data.data!;
}
